import React, { forwardRef } from "react";
import {
  Button,
  CheckmarkCircleIcon,
  Flex,
  NotesIcon,
  SendIcon,
  Text,
} from "@fluentui/react-northstar";
import { Card, Col, Progress, Row } from "antd";
import "./FinalEvaluation.scss";
import { useTranslation } from "react-i18next";
import { useIntelligentGrading } from "../../../../../context/useIntelligentGrading";

const FinalEvaluation = forwardRef(
  (
    {
      gradingResult,
      rubrics,
      overallScore,
      grade,
      onGenerateEvaluationReport,
      handleSubmitGrading,
      isLoading,
      isEditingFeedback,
      submission,
      sendGradingReport,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const translation = t("assignment").summary.single;
    const { isSendingReport } = useIntelligentGrading();

    return (
      <Card
        title={translation.finalEvaluation.title}
        style={{ marginTop: 15 }}
        className="final-evaluation"
      >
        <Flex column gap="gap.small">
          <Row gutter={[15, 15]}>
            <Col xs={24} sm={24} md={24} lg={12} xl={12}>
              <Flex gap="gap.smaller" column>
                <Text
                  content={translation.finalEvaluation.performanceSummary}
                  weight="semibold"
                />
                <div
                  dangerouslySetInnerHTML={{
                    __html: gradingResult.summary,
                  }}
                />
              </Flex>
            </Col>
            <Col xs={24} sm={24} md={24} lg={12} xl={12}>
              <Flex gap="gap.smaller" column>
                <Text
                  content={translation.finalEvaluation.rubricScores}
                  weight="semibold"
                />

                <Flex column>
                  {rubrics.map((rubric, i) => (
                    <Flex column key={i + 1}>
                      <Flex space="between">
                        <Text size="small" content={rubric.label} />
                        <Text
                          size="small"
                          weight="semibold"
                          content={`${rubric.score}/${rubric.max}`}
                        />
                      </Flex>
                      <Progress
                        className="rubric-progress"
                        size="small"
                        percent={Math.round((rubric.score / rubric.max) * 100)}
                        showInfo={false}
                        strokeColor={rubric.color}
                        style={{ height: "fit-content" }}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </Col>
            <Col span={24}>
              <div className="result-wrapper">
                <Flex gap="gap.smaller" column>
                  <Text
                    align="center"
                    size="larger"
                    content={`${overallScore.score}/${overallScore.max} ${translation.finalEvaluation.points}`}
                    weight="bold"
                  />
                  <Text
                    align="center"
                    size="large"
                    content={`${Math.round(
                      (overallScore.score / overallScore.max) * 100
                    )}% - ${translation.finalEvaluation.grade}: ${grade}`}
                    weight="semibold"
                    color="brand"
                  />
                  <Text
                    align="center"
                    content={`${translation.finalEvaluation.aiConfidence}: ${gradingResult.overallConfidence}% | ${translation.finalEvaluation.formatsAnalyzed}: ${gradingResult.formates.analyzed}/${gradingResult.formates.total}`}
                  />
                </Flex>
              </div>
            </Col>
          </Row>
          <Flex gap="gap.smaller" fill>
            {submission.submissionStatus !== "Completed" && (
              <Button
                icon={<CheckmarkCircleIcon />}
                content={translation.finalEvaluation.approveFinalGrade}
                onClick={() => handleSubmitGrading("Completed")}
                disabled={isLoading || isEditingFeedback}
                loading={isLoading}
                primary
                styles={{
                  backgroundColor: "#52c41a",
                  background: "#52c41a",
                  borderColor: "#52c41a",
                  ":hover": {
                    background: "#43a047",
                    borderColor: "#43a047",
                  },
                }}
                fluid
              />
            )}
            <Button
              icon={<NotesIcon />}
              onClick={onGenerateEvaluationReport}
              content={translation.finalEvaluation.generateReport}
              className="generate-report-button"
              fluid
              disabled={isLoading || isEditingFeedback}
            />
            <Button
              icon={<SendIcon />}
              content={translation.finalEvaluation.sendFeedback}
              fluid
              disabled={isLoading || isEditingFeedback}
              loading={isSendingReport}
              onClick={() => sendGradingReport(ref)}
            />
          </Flex>
        </Flex>
      </Card>
    );
  }
);

export default FinalEvaluation;
